import { useEffect, useState } from 'react';

import { Divider, Tag, useDisclosure } from '@chakra-ui/react';
import { differenceInSeconds } from 'date-fns';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { ErrorIndicator } from 'components/ErrorIndicator';
import { Card, Text, VStack } from 'components/uikit';
import { CardRow } from 'components/uikit/Card/CardRow';
import { Acquirer } from 'data/merchant/models/acquirer';
import { notifyAboutClientPaymentAtom } from 'data/payments/atoms';
import { usePollPayment } from 'data/payments/hooks';
import { DepositCredentials, DepositMethod } from 'data/payments/models';
import { useUploadDepositClientFiles } from 'data/traders/deposits/hooks/useUploadDepositClientFiles';
import { identify, trackEvent } from 'utils/analytics';

import { PaymentClientControls } from './PaymentClientControls';
import { PaymentStatusControls } from './PaymentStatusControls';
import { PaymentUploadModal } from './PaymentUploadModal';

interface PaymentCredentialsProps {
  id: string;
  acquirer: Acquirer;
  method: DepositMethod | null;
  amount: string;
  currency: string;
  expiryDate: Date;
  credentials: DepositCredentials;
  failureRedirectLink: string | null;
  customerId?: string | null;
}

const calculateTimeLeft = (expiryDate: Date) => {
  const secondsDiff = Math.max(0, differenceInSeconds(expiryDate, new Date()));

  return `${Math.floor(secondsDiff / 60)}:${String(secondsDiff % 60).padStart(
    2,
    '0',
  )}`;
};

export function PaymentCredentials({
  id,
  acquirer,
  method,
  amount,
  currency,
  expiryDate,
  credentials,
  failureRedirectLink,
  customerId,
}: PaymentCredentialsProps) {
  const { t } = useTranslation('payments');
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(expiryDate));

  const [_, notify] = useAtom(notifyAboutClientPaymentAtom);
  const storageKey = `payment-paid-${id}`;

  const [paid, setPaid] = useState(
    () => localStorage.getItem(storageKey) === 'true',
  );
  usePollPayment(id);
  const { isUploadLoading, onUploadComplete } = useUploadDepositClientFiles(id);

  const uploadDisclosure = useDisclosure();

  useEffect(() => {
    setInterval(() => {
      setTimeLeft(calculateTimeLeft(expiryDate));
    }, 1000);
  }, [setTimeLeft, expiryDate]);

  useEffect(() => {
    trackEvent('deposit-credentials-displayed');
  }, []);

  useEffect(() => {
    if (customerId) {
      identify({ user: { id: customerId } });
    }
  }, [customerId]);

  return (
    <VStack spacing={['12px', '24px']} px='16px' maxW='540px'>
      <VStack spacing='0' align='center' alignSelf='center'>
        <Text
          as='h1'
          fontSize='2xl'
          fontWeight='medium'
          color='grey.primaryText'
          textAlign='center'
        >
          {t(paid ? 'payment_paid_wait' : 'payment_credentials_title')}
        </Text>
      </VStack>
      <Card>
        <CardRow
          copy
          label={t('payment_amount')}
          value={amount}
          displayedValue={
            <>
              {amount}
              <sub style={{ bottom: 0 }}>{currency}</sub>
            </>
          }
        />
        <Divider />
        <CardRow
          copy
          label={t(
            credentials.type === 'SBP'
              ? 'payment_phone_number'
              : 'payment_card_number',
          )}
          value={credentials.value}
        />
        {credentials.bank ? (
          <>
            <Divider />
            <CardRow copy label={t('payment_bank')} value={credentials.bank} />
          </>
        ) : null}
        {credentials.name ? (
          <>
            <Divider />
            <CardRow
              copy
              label={t('payment_recepient')}
              value={credentials.name}
            />
          </>
        ) : null}
        {method === 'SBER_AZN' ? (
          <>
            <Divider />
            <CardRow
              label={t('payment_sender_sber')}
              value=''
              displayedValue={<ErrorIndicator />}
            />
          </>
        ) : null}
        <Divider />
        <CardRow label={t('payment_expiry')} value={timeLeft} />
      </Card>
      {credentials.type === 'SBP' ? (
        <Tag
          borderRadius='8px'
          textAlign='center'
          py='8px'
          colorScheme='yellow'
          fontSize='lg'
        >
          {t('payment_notice_sbp', { bank: credentials.bank })}
        </Tag>
      ) : null}
      {acquirer === 'TEST' ? (
        <PaymentStatusControls id={id} />
      ) : (
        <PaymentClientControls
          id={id}
          failureRedirectLink={failureRedirectLink}
          onPaid={() => {
            uploadDisclosure.onOpen();
          }}
        />
      )}
      <PaymentUploadModal
        isOpen={uploadDisclosure.isOpen}
        onClose={uploadDisclosure.onClose}
        isLoading={isUploadLoading}
        onUpload={async (formData) => {
          await onUploadComplete(formData);
          notify({ depositId: id });
          if (!paid) {
            setPaid(true);
          }
        }}
      />
    </VStack>
  );
}
